// /app/frontend/src/pages/SharedComparisonPage.jsx
// Public page for viewing shared fabric comparisons

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Scale, Package, Loader2, ArrowLeft, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { resolveImageUrl } from '../lib/imageCache';
import { brandLabel } from '../lib/brandNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedComparisonPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchColors = async () => {
      // Get color IDs from URL params
      const colorIds = searchParams.get('colors')?.split(',') || [];
      
      if (colorIds.length === 0) {
        setError('No colors specified');
        setLoading(false);
        return;
      }

      try {
        // Fetch color details from the API
        const response = await axios.get(`${API_URL}/api/fabric-collections/colors/batch`, {
          params: { ids: colorIds.join(',') }
        });
        setColors(response.data.colors || []);
      } catch (err) {
        console.error('Failed to fetch colors:', err);
        // Fallback: try to parse color data from URL if included
        const colorData = searchParams.get('data');
        if (colorData) {
          try {
            const parsed = JSON.parse(atob(colorData));
            setColors(parsed);
          } catch (e) {
            setError('Failed to load comparison data');
          }
        } else {
          setError('Failed to load comparison data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchColors();
  }, [searchParams]);

  const handleRequestSamples = () => {
    // Store colors for sample request
    const samplesData = colors.map(c => ({
      color_id: c.id || c.color_id,
      color_name: c.name || c.color_name,
      color_code: c.code || c.color_code || '',
      hex_color: c.hex_color || '',
      image_url: c.image_url || '',
      material_name: c.material_name || '',
      collection_name: c.collection_name || '',
      brand_name: c.brand_name || ''
    }));
    localStorage.setItem('sampleRequestColors', JSON.stringify(samplesData));
    navigate('/request-samples');
    toast.success(`${colors.length} colors added to sample request`);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-6xl mx-auto px-4 py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
            <p className="mt-4 text-neutral-500">Loading comparison...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || colors.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <Scale className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
            <h1 className="text-2xl font-bold text-neutral-700 mb-2">
              {error || 'No Colors to Compare'}
            </h1>
            <p className="text-neutral-500 mb-6">
              The comparison link may be invalid or expired.
            </p>
            <Button
              onClick={() => navigate('/products/roller-shades')}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Browse Fabrics
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Scale className="w-8 h-8 text-amber-600" />
                  <h1 className="text-3xl font-bold text-neutral-900">Fabric Comparison</h1>
                </div>
                <p className="text-neutral-500">
                  Comparing {colors.length} fabric colors side by side.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={copyShareLink}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </Button>
                <Button
                  onClick={handleRequestSamples}
                  className="bg-amber-600 hover:bg-amber-700 gap-2"
                >
                  <Package className="w-4 h-4" />
                  Request These Samples
                </Button>
              </div>
            </div>
          </div>

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {colors.map((color, index) => (
              <motion.div
                key={color.id || color.color_id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
              >
                {/* Color Swatch */}
                <div 
                  className="aspect-square w-full"
                  style={{ backgroundColor: color.hex_color || '#E5E5E5' }}
                >
                  {color.image_url && (
                    <img 
                      src={resolveImageUrl(color.image_url, 256)} 
                      alt={color.name || color.color_name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                
                {/* Details */}
                <div className="p-4 space-y-2">
                  <h3 className="font-bold text-lg text-neutral-900">
                    {color.name || color.color_name}
                  </h3>
                  
                  {(color.code || color.color_code) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Code</span>
                      <span className="font-medium">{color.code || color.color_code}</span>
                    </div>
                  )}
                  
                  {color.hex_color && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-neutral-500">Hex</span>
                      <span className="font-mono text-xs bg-neutral-100 px-2 py-1 rounded">{color.hex_color}</span>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t border-neutral-100 space-y-1">
                    {color.brand_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Brand</span>
                        <span className="font-medium">{brandLabel(color.brand_name)}</span>
                      </div>
                    )}
                    {color.collection_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Collection</span>
                        <span className="font-medium">{color.collection_name}</span>
                      </div>
                    )}
                    {color.material_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Material</span>
                        <span className="font-medium">{color.material_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SharedComparisonPage;
